import { Box, TextField } from '@mui/material';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

interface AddressFieldNames<TFieldValues extends FieldValues> {
  addressLine1: FieldPath<TFieldValues>;
  addressLine2: FieldPath<TFieldValues>;
  city: FieldPath<TFieldValues>;
  state: FieldPath<TFieldValues>;
  country: FieldPath<TFieldValues>;
  postalCode: FieldPath<TFieldValues>;
  email: FieldPath<TFieldValues>;
}

interface AddressFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  fieldNames: AddressFieldNames<TFieldValues>;
  disabled?: boolean;
  displayEmail?: boolean;
}

export const AddressFields = <TFieldValues extends FieldValues>({
  control,
  fieldNames,
  disabled = false,
  displayEmail = true,
}: AddressFieldsProps<TFieldValues>) => {
  const readOnlyInputProps = disabled ? { readOnly: true } : undefined;

  return (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '100%', gap: '8px' }}>
    <Controller
      name={fieldNames.addressLine1}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          size="small"
          id="qa-addressLine1-ipt"
          label="Address line 1"
          InputProps={readOnlyInputProps}
          inputProps={{ maxLength: 60 }}
          sx={{ mb: 0 }}
        />
      )}
    />

    <Controller
      name={fieldNames.addressLine2}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          size="small"
          id="qa-addressLine2-ipt"
          label="Address line 2"
          InputProps={readOnlyInputProps}
          inputProps={{ maxLength: 60 }}
          sx={{ mb: 0 }}
        />
      )}
    />

    <Controller
      name={fieldNames.city}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          size="small"
          id="qa-city-ipt"
          label="City"
          InputProps={readOnlyInputProps}
          inputProps={{ maxLength: 40 }}
          sx={{ mb: 0 }}
        />
      )}
    />

    <Box sx={{ display: 'flex', maxWidth: '100%', minWidth: 0, gap: '15px', mt: 0 }}>
      <Box sx={{ flex: '1 1 33%' }}>
        <Controller
          name={fieldNames.state}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              id="qa-state-ipt"
              label="State/Prov"
              InputProps={readOnlyInputProps}
              inputProps={{ maxLength: 3 }}
              sx={{ mb: 0 }}
            />
          )}
        />
      </Box>

      <Box sx={{ flex: '1 1 33%' }}>
        <Controller
          name={fieldNames.country}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              id="qa-country-ipt"
              label="Country"
              InputProps={readOnlyInputProps}
              inputProps={{ maxLength: 3 }}
              sx={{ mb: 0 }}
            />
          )}
        />
      </Box>

      <Box sx={{ flex: '1 1 34%' }}>
        <Controller
          name={fieldNames.postalCode}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              id="qa-postalCode-ipt"
              label="Postal Code"
              InputProps={readOnlyInputProps}
              inputProps={{ maxLength: 10 }}
              sx={{ mb: 0 }}
            />
          )}
        />
      </Box>
    </Box>

    {displayEmail ? (
      <Controller
        name={fieldNames.email}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            id="qa-email-ipt"
            label="Email"
            InputProps={readOnlyInputProps}
            inputProps={{ maxLength: 240 }}
            sx={{ mb: 0 }}
          />
        )}
      />
    ) : null}
  </Box>
  );
};
